import { queryOptions, useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";

import { explosivesAjax } from "../../ajax/ExplosivesAjax";
import { personnelAjax } from "../../ajax/PersonnelAjax";
import { ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import { FlatGroupOption } from "../../DTOs/Common/FlatGroupOption";
import { ExplosiveMaterialOptionDTO, MagazineStockDTO, PersonOptionDTO } from "../../DTOs/DrillBlast/ExplosivesDTO";

export const EXPIRY_WARNING_DAYS = 30;

export const expiresSoon = (expires?: DateTime | null) => expires != null && expires.diffNow("days").days < EXPIRY_WARNING_DAYS;

export const explosivesStockQuery = queryOptions({
  queryKey: ['drillBlast', 'explosivesStock'],
  queryFn: async (): Promise<MagazineStockDTO[]> => {
    const res = await explosivesAjax.getStock();
    if (ErrorResponse.isErrorResponse(res)) throw new Error('Failed to load the magazines');
    return res.data;
  },
});

export const explosiveMaterialsQuery = queryOptions({
  queryKey: ['references', 'drillBlast', 'explosiveMaterials'],
  queryFn: async (): Promise<ExplosiveMaterialOptionDTO[]> => {
    const res = await explosivesAjax.getMaterials();
    if (ErrorResponse.isErrorResponse(res)) throw new Error('Failed to load the explosives products');
    return res.data;
  },
  staleTime: 5 * 60 * 1000,
});

export const personOptionsQuery = queryOptions({
  queryKey: ['references', 'drillBlast', 'personOptions'],
  queryFn: async (): Promise<PersonOptionDTO[]> => {
    const res = await personnelAjax.getOptions();
    if (ErrorResponse.isErrorResponse(res)) throw new Error('Failed to load the people');
    return res.data;
  },
  staleTime: 5 * 60 * 1000,
});

export const openBlastsQuery = queryOptions({
  queryKey: ['drillBlast', 'openBlasts'],
  queryFn: async (): Promise<FlatGroupOption[]> => {
    const res = await explosivesAjax.getOpenBlasts();
    if (ErrorResponse.isErrorResponse(res)) throw new Error('Failed to load the blasts');
    return res.data;
  },
  staleTime: 60 * 1000,
});

const NONE: never[] = [];

export const useExplosiveMaterials = () => useQuery(explosiveMaterialsQuery).data ?? NONE;
export const usePersonOptions = () => useQuery(personOptionsQuery).data ?? NONE;
export const useOpenBlasts = (enabled: boolean) => useQuery({ ...openBlastsQuery, enabled }).data ?? NONE;
